// Tebox Design System
// Centralized design tokens for the Tebox design system

/** UI font — Inter (Google Fonts in `apps/web` / `apps/booking` `index.html`) */
const APP_FONT = "'Inter', sans-serif";

/** Shared tracking for H3-scale titles (subsection / drawer chrome) — Phase C. */
const TYPOGRAPHY_H3_LETTER_SPACING = '-0.01em';

/** Soft chrome — cards, buttons, fields, modals, drawers, status pills (TEBOX `radius-container`). */
const RADIUS_CONTAINER = '6px';

/** Pill/badge full radius — avatars and circular icon controls only (not status label pills). */
const RADIUS_FULL = '9999px';

/** Success / payment green — saturated enough for badges, calm enough for tables (avoids neon #2EFF85 on long reads). */
const ACCENT_SUCCESS_HEX = '#22C55E';
/** Pill and badge label size — matches `typography.body` (13px). */
const PILL_FONT_SIZE = '13px';
/** Darker stroke on success fills (toggle on track edge, paired with {@link ACCENT_SUCCESS_HEX}). */
const ACCENT_SUCCESS_STROKE_HEX = '#16A34A';

/**
 * Structural UI border — form outlines, cards, menus, table shells, drawer sections.
 * Spec: `#D8DBE0` (TEBOX / AI_CONTEXT). Interior hairlines use {@link TABLE_GRID_DIVIDER_HEX}.
 */
const BORDER_FIELD = '#D8DBE0';

/**
 * Form infield label — readable step on the cool grey text ramp (not the border token).
 * Same value as `colors.text.secondary`.
 */
const FORM_FIELD_LABEL_HEX = '#3A4249' as const;

/**
 * Interior hairlines for `StandardTable*` (row + column rules) — lighter than {@link BORDER_FIELD}.
 * Matches `--color-table-row-divider` in `index.css`. (~15% lighter than `#E5E7EB`.)
 */
const TABLE_GRID_DIVIDER_HEX = '#E9EBEE' as const;

/** Tebox accent fills for two-segment donut charts (see `components.charts.donutTwoSegment`). */
const CHART_DONUT_TWO_SEGMENT = ['#2E96FF', '#E1FF1B'] as const;

/** Slice label ink for {@link CHART_DONUT_TWO_SEGMENT} — white on blue, black on lime. */
const CHART_DONUT_TWO_SEGMENT_DATA_LABEL_COLORS = ['#FFFFFF', '#000000'] as const;

/** Tebox accent fills for multi-segment distribution charts (reporting booked revenue, etc.). */
const CHART_DONUT_MULTI_SEGMENT = ['#2E96FF', '#E1FF1B', '#FF2EB9', '#F1FFD6', '#2E96FF'] as const;

/** Ledger / all-transactions report — Source column pill ink (see `colors.ledgerTransactionSource`). */
const LEDGER_TX_SOURCE = {
  wallet: '#2E96FF',
  reservation: '#FFA82E',
  gateway: '#FF2EB9',
  voidRefund: FORM_FIELD_LABEL_HEX,
  proShop: '#047857',
  giftCard: '#7C3AED',
  onlineShop: '#0284c7',
  pass: '#0D9488',
  staffSale: '#4F46E5',
  membership: '#B45309',
  purchase: '#021B1B',
  default: '#000000',
} as const;

/** Consolidated floating-toolbar activity banner — success family (matches `accent.primaryLighter` / `primaryDarker`, TEBOX spec). */
const FLOATING_TOOLBAR_ACTION_BANNER_SUCCESS = {
  bg: '#F1FFD6',
  border: '#B4CC16',
  text: '#021B1B',
  icon: '#B4CC16',
} as const;

/** Removal / neutral confirmations — secondary surface, grid border. */
const FLOATING_TOOLBAR_ACTION_BANNER_NEUTRAL = {
  bg: '#F3F4F6',
  border: BORDER_FIELD,
  text: '#021B1B',
  icon: FORM_FIELD_LABEL_HEX,
} as const;

/** Errors and destructive outcomes — status error surface (pairs with `colors.status.error`). */
const FLOATING_TOOLBAR_ACTION_BANNER_ERROR = {
  bg: '#FEE2E2',
  border: '#EF4444',
  text: '#021B1B',
  icon: '#EF4444',
} as const;

/** White check on lime (`components.formCheckbox`). */
const FORM_CHECK_ICON_DATA_URL = `url("data:image/svg+xml;charset=utf-8,${encodeURIComponent(
  '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24"><path fill="none" stroke="#FFFFFF" stroke-width="4.5" stroke-linecap="round" stroke-linejoin="round" d="M5 12.8 L11.2 18.8 L21 8"/></svg>'
)}")`;

/**
 * Single-line controls: MUI OutlinedInput, FormInput, FormSelect, SimpleDatePicker, MaterialOutlinedTextField.
 * 48px — comfortable, WCAG 2.5.5 AAA target size. `button.sizes.medium` is 40px (IOSButton);
 * inputs stay taller for touch/legibility. Iterated 46.08 → 48 → 36 → 38 → 48; the final 48 commits to comfort over density.
 */
const MATERIAL_OUTLINED_MIN_HEIGHT = '48px';
const MATERIAL_OUTLINED_PADDING_Y = '14px';
const MATERIAL_OUTLINED_PADDING_X = '12px';
const MATERIAL_OUTLINED_SELECT_ICON_GUTTER = '28px';
/** Multiline bottom padding — slightly tighter than top so multi-line content doesn't bloat. */
const MATERIAL_OUTLINED_MULTILINE_PADDING_BOTTOM = '12px';

/** Horizontal inset for page chrome, table cells, toolbars, and settings cards (aligns with StandardTableV2). */
const CONTENT_GUTTER = '27px';

/**
 * Default muted surface for pages, tables, hovers, cards — light warm off-white (not sidebar).
 * Sidebar rail uses `BACKGROUND_SIDEBAR_RAIL` (#F2F0ED); Read/Club Pulse canvas uses `BACKGROUND_SIDEBAR_BEIGE`.
 */
const BACKGROUND_SURFACE = '#FAFAF9' as const;

/** Staff-blocked tee times — very light grey surfaces + grid-aligned borders (`banner`, cells, row stripes). */
const TEE_SHEET_BLOCKED_SURFACE = '#FAFAFA';
const TEE_SHEET_BLOCKED_SURFACE_HOVER = '#F3F4F6';
const TEE_SHEET_BLOCKED_BORDER = BORDER_FIELD;
/** Muted copy/icons on blocked surfaces — aligns with `colors.text.secondary`. */
const TEE_SHEET_BLOCKED_LABEL = FORM_FIELD_LABEL_HEX;
/** Paid status chip on tee sheet / reservation modal — brand lime (`accent.primary`); use dark ink on fill. */
const TEE_SHEET_PAID_CHIP_BACKGROUND = '#E1FF1B';

/**
 * Left nav rail (IOSSidebar) — warm beige `#F2F0ED`. Reads as a discrete band
 * against the muted canvas, giving the rail visual structure. Earlier iteration
 * tried `#FAF9F8` — too close to canvas; the rail disappeared. Restored to the
 * legacy beige to recover sidebar identity.
 */
const BACKGROUND_SIDEBAR_RAIL = '#F2F0ED' as const;

/**
 * "The Read" home canvas + Club Pulse dashboard canvas — warm off-white `#FAF9F8`.
 * A quieter wash used where the surface IS the page (not a rail). Kept separate
 * from the sidebar rail so each surface can move independently.
 *
 * Consumed via `colors.background.sidebarCanvas` (Layout.tsx for The Read,
 * club-pulse-dashboard-tokens.ts for Club Pulse).
 */
const BACKGROUND_SIDEBAR_BEIGE = '#FAF9F8' as const;

/**
 * Primary destination labels — IOSSidebar + settings corridor.
 * Body scale (13px) at medium weight; do not reuse tee-sheet `playerName`.
 * See: TEBOX_DESIGN_SYSTEM.html #layout-sidebar-nav, #components-settings-corridor
 */
const SIDEBAR_NAV_LABEL = {
  fontFamily: APP_FONT,
  fontSize: '13px',
  fontWeight: 500 as const,
  lineHeight: 1.5,
  letterSpacing: 'normal' as const,
} as const;

/** Between xs (4px) and sm (8px); KPI / tight metric stacks only. */
const MICRO_GAP = '6px';

/** Inter for currency / ledger amounts — proportional figures. */
const CURRENCY_AMOUNT_FONT = {} as const;

/** @deprecated alias — use {@link CURRENCY_AMOUNT_FONT} */
const TABLE_TOTAL_AMOUNT_NUMERIC = CURRENCY_AMOUNT_FONT;

/** Unified amount style for integers + fractional digits (above-table totals / `Money` `tableTotal`). */
const TABLE_TOTAL_LG_AMOUNT = {
  fontSize: '36px',
  fontWeight: 500,
  lineHeight: '36px',
  letterSpacing: 'normal',
  ...TABLE_TOTAL_AMOUNT_NUMERIC,
} as const;

const TABLE_TOTAL_LG_DECIMAL = {
  ...TABLE_TOTAL_LG_AMOUNT,
  fontSize: '27px',
  lineHeight: '27px',
} as const;

const TABLE_TOTAL_MD_AMOUNT = {
  fontSize: '28px',
  fontWeight: 500,
  lineHeight: '30px',
  letterSpacing: 'normal',
  ...TABLE_TOTAL_AMOUNT_NUMERIC,
} as const;

const TABLE_TOTAL_MD_DECIMAL = {
  ...TABLE_TOTAL_MD_AMOUNT,
  fontSize: '21px',
  lineHeight: '23px',
} as const;

const TABLE_TOTAL_SM_AMOUNT = {
  fontSize: '20px',
  fontWeight: 500,
  lineHeight: '24px',
  letterSpacing: 'normal',
  ...TABLE_TOTAL_AMOUNT_NUMERIC,
} as const;

const TABLE_TOTAL_SM_DECIMAL = {
  ...TABLE_TOTAL_SM_AMOUNT,
  fontSize: '15px',
  lineHeight: '18px',
} as const;

/** Table KPI card / KpiStrip primary metric — integer + 75% decimal (matches `Money` `tableKpi`). */
const TABLE_KPI_VALUE = {
  fontSize: '28px',
  fontWeight: 500,
  letterSpacing: '-0.02em',
  lineHeight: 1.1,
  ...TABLE_TOTAL_AMOUNT_NUMERIC,
} as const;

const TABLE_KPI_VALUE_DECIMAL = {
  ...TABLE_KPI_VALUE,
  fontSize: '21px',
} as const;

const SPACING_PRIMITIVES = {
  xs: '4px',
  sm: '8px',
  md: '16px',
  lg: '24px',
  xl: '32px',
  '2xl': '48px',
  '3xl': '64px',
  '4xl': '96px',
} as const;

/** Shared popover list — filter bars, `FormSelect`, `MultiSelectDropdown`, `TableDropdown`. */
const DROPDOWN_MENU_TOKENS = {
  offsetBelowField: SPACING_PRIMITIVES.sm,
  offsetBelowTrigger: '4px',
  maxHeight: '280px',
  /** In-flow menus (filter bars). */
  zIndex: 1200,
  /** Portaled menus above slide modals (`IOSModal` ~1300). */
  portalZIndex: 1400,
  minWidth: '176px',
  rowPaddingX: SPACING_PRIMITIVES.md,
  rowPaddingY: '10px',
  sectionPaddingX: SPACING_PRIMITIVES.md,
  sectionPaddingY: SPACING_PRIMITIVES.sm,
} as const;

/**
 * StandardTableV2 vertical rhythm. `StandardTableV2` defaults to `compact` for operational lists.
 * Choose `comfortable` for sparse, hero rows (see RAMP Phase 3 / TEBOX table density).
 */
const STANDARD_TABLE_DENSITY = {
  comfortable: {
    paddingHeaderFirst: `7px ${CONTENT_GUTTER}`,
    paddingHeaderRest: `10px ${CONTENT_GUTTER}`,
    paddingBodyFirst: `11px ${CONTENT_GUTTER}`,
    paddingBodyRest: `20px ${CONTENT_GUTTER}`,
  },
  compact: {
    paddingHeaderFirst: `5px ${CONTENT_GUTTER}`,
    paddingHeaderRest: `8px ${CONTENT_GUTTER}`,
    paddingBodyFirst: `10px ${CONTENT_GUTTER}`,
    paddingBodyRest: `18px ${CONTENT_GUTTER}`,
  },
} as const;

export type StandardTableDensityMode = keyof typeof STANDARD_TABLE_DENSITY;

export const teboxDesignSystem = {
  colors: {
    primary: '#000000',
    primaryLight: '#1a1a1a',
    background: {
      primary: '#FFFFFF',
      /** Inbox, table bands, page tint, row hover — light warm off-white (`BACKGROUND_SURFACE`). */
      secondary: BACKGROUND_SURFACE,
      /** Nested / inset surfaces — same fill as `secondary`; name encodes structure. */
      tertiary: BACKGROUND_SURFACE,
      /** KPI tiles, completed rows — same fill as `secondary`; name encodes role. */
      highlight: BACKGROUND_SURFACE,
      /** Interactive hover (lists, tables, chips) — same hex as muted surfaces. */
      hover: BACKGROUND_SURFACE,
      /** Left nav rail — warm beige `#F2F0ED` (distinct from `sidebarCanvas`) */
      sidebar: BACKGROUND_SIDEBAR_RAIL,
      /** The Read home + Club Pulse canvas — quieter warm off-white `#FAF9F8` */
      sidebarCanvas: BACKGROUND_SIDEBAR_BEIGE},
    text: {
      /** Primary UI copy — matches typography.body (TEBOX_DESIGN_SYSTEM.html) */
      primary: '#021B1B',
      secondary: FORM_FIELD_LABEL_HEX,
      tertiary: '#646870',
      disabled: '#86888C',
      inverse: '#FFFFFF',
      /** Left rail (`IOSSidebar`): default labels + icons. Active route uses `primary`. */
      sidebarNav: '#474747'},
    accent: {
      // Accent palette centered on #E1FF1B (see apps/web/src/index.css and docs/design-system/TEBOX_DESIGN_SYSTEM.html)
      primary: '#E1FF1B',
      primaryLight: '#ECFF41',
      primaryLighter: '#F1FFD6',
      primaryDark: '#CCE618',
      primaryDarker: '#B4CC16',
      /**
       * Brand complementary teal — calm depth next to lime primary.
       * Use surgically for deep brand marks, rare branded secondary actions, and lime+teal chart pairs.
       * Do not use for body text, greys, status, or primary lime CTAs.
       */
      complementary: '#186466',
      secondary: '#2E96FF',
      tertiary: '#FF2EB9',
      quaternary: '#FFA82E',
      /** Pale orange wash — pairs with quaternary for secondary emphasis surfaces (behind lime primary actions). */
      quaternaryLighter: '#FFF5EB',
      /** Soft peach border on quaternaryLighter — warmer than structural grey. */
      quaternaryStroke: '#FFE8CC',
      success: ACCENT_SUCCESS_HEX,
      warning: '#FFA82E',
      error: '#FF2E51',
      info: '#2E96FF'},
    border: {
      light: BORDER_FIELD, /* Structural grid — unified with form outlines + reservation/table shells */
      /** Native + MUI outlined default — same as structural `light` (unified field system). */
      field: BORDER_FIELD,
      /** Alias of `field` / `light` — kept for older references. */
      fieldMaterialOutlined: BORDER_FIELD,
      medium: BORDER_FIELD,
      dark: '#9CA3AF'},
    // Gray scale for backward compatibility
    gray: {
      50: '#FAFAF9',
      100: '#F3F4F6',
      200: TABLE_GRID_DIVIDER_HEX,
      300: BORDER_FIELD,
      400: '#86888C',
      500: FORM_FIELD_LABEL_HEX,
      600: '#4B5563',
      700: '#374151',
      800: '#1F2937',
      900: '#111827'},
    table: {
      border: BORDER_FIELD,
      /** Horizontal + vertical rules inside standardized tables — softer than structural `colors.border.light`. */
      rowDivider: TABLE_GRID_DIVIDER_HEX,
      headerBg: BACKGROUND_SURFACE,
      /** TEBOX table tbody row hover — match `colors.background.hover`. */
      rowHover: BACKGROUND_SURFACE,
      /** StandardTableCell / StandardTableV2 body — first column (`density.comfortable`) */
      cellPaddingFirstColumn: STANDARD_TABLE_DENSITY.comfortable.paddingBodyFirst,
      /** StandardTableCell — other columns, comfortable density (20px vertical) */
      cellPaddingDefault: STANDARD_TABLE_DENSITY.comfortable.paddingBodyRest,
      /** StandardTableHeaderCellV2 — first column */
      headerCellPaddingFirst: STANDARD_TABLE_DENSITY.comfortable.paddingHeaderFirst,
      /** StandardTableHeaderCellV2 — other columns */
      headerCellPaddingDefault: STANDARD_TABLE_DENSITY.comfortable.paddingHeaderRest,
      /** Shared horizontal inset for table cells (use with form fields for toolbar alignment) */
      cellPaddingInline: CONTENT_GUTTER},
    separator: {
      opaque: BACKGROUND_SURFACE,
      transparent: 'rgba(0, 0, 0, 0.15)'},
    status: {
      success: ACCENT_SUCCESS_HEX,
      warning: '#FF9500',
      error: '#EF4444',
      /** Inline form / banner error surface (pairs with `status.error`) */
      errorLight: '#FEE2E2',
      /** Soft success wash (schedules, confirmations) — pairs with `status.success` */
      successLight: '#f0fdf4',
      /** Soft warning / upcoming wash — pairs with `status.warning` */
      warningLight: '#fef3c7',
      info: '#021B1B'},
    /** Unified ledger / all-transactions report — Source column pill colors */
    ledgerTransactionSource: { ...LEDGER_TX_SOURCE },
    // Tee sheet banners (memo/notes vs light-grey blocked strips)
    teeSheet: {
      groupBannerBg: '#F1FFD6',
      groupBannerBgHover: '#E2EFB2',
      /**
       * Combined memo/notes strip — match `TEBOX_DESIGN_SYSTEM.html` notes/memo strip (bordered demo:
       * cool / warm tints; flush uses highlight/secondary). Bordered tints are used in-app for a bit
       * more life on the tee sheet without a full border.
       */
      combinedBannerMemoText: '#1E3A5F',
      combinedBannerNotesText: '#8B6914',
      combinedBannerMemoBackground: '#F8FAFF',
      combinedBannerNotesBackground: '#FDFFF8',
      /** Subtle nudge from base (avoid strong tinted hovers; users map hovers to “a bit darker”, not a big color jump). */
      combinedBannerMemoBackgroundHover: '#F3F4F8',
      combinedBannerNotesBackgroundHover: '#F5F6F0',
      /**
       * Blocked tee times — banner strips (`TeeSheetPanelView`, `BlockedTimesBanner`), blocked `PlayerCell`,
       * and blocked-row stripes (`TeeTimeRow`). Light grey fills; labels/icons use secondary grey (`blockedCellLabel`).
       */
      blockedCellBackground: TEE_SHEET_BLOCKED_SURFACE,
      blockedCellBackgroundHover: TEE_SHEET_BLOCKED_SURFACE_HOVER,
      blockedCellBorder: TEE_SHEET_BLOCKED_BORDER,
      blockedCellLabel: TEE_SHEET_BLOCKED_LABEL,
      /** Reservation status chip when the spot is paid — lime fill, dark icon. */
      paidChipBackground: TEE_SHEET_PAID_CHIP_BACKGROUND,
    }
  },
  spacing: {
    ...SPACING_PRIMITIVES,
    /**
     * Semantic aliases (RAMP-aligned console rhythm — docs/design/RAMP_UI_UX_ALIGNMENT.md).
     * Prefer these names in new layout code so page/toolbar/table gutters stay predictable.
     */
    /** Same as CONTENT_GUTTER — primary horizontal page inset (layout header + scroll region + tables). */
    pageInset: CONTENT_GUTTER,
    /** Same as CONTENT_GUTTER — aligns filters/toolbars with StandardTableV2 columns. */
    contentGutter: CONTENT_GUTTER,
    /** Alias sm — related controls (label-to-field gap, chip rows). */
    stackGapTight: SPACING_PRIMITIVES.sm,
    /** Alias md — default gap between form fields / stacked blocks. */
    stackGap: SPACING_PRIMITIVES.md,
    /** Alias lg — break between titled sections or title block to primary content. */
    sectionGap: SPACING_PRIMITIVES.lg,
    /** KPI / metric micro-stack only (not a4px-grid step). */
    microGap: MICRO_GAP},
  typography: {
    fontFamily: APP_FONT,

    /**
     * STRICT DESIGN SYSTEM: Only 7 typography tokens as defined in TEBOX_DESIGN_SYSTEM.html
     * All other font sizes should map to these tokens.
     * See: docs/design-system/TYPOGRAPHY_AUDIT_AND_REFACTORING_PLAN.md
     * All typography uses Inter ({@link APP_FONT}).
     */

    // H1 — Headline Large (M3 role). Phase C: one page title.
    h1: {
      fontSize: '32px',
      fontWeight: 700,
      lineHeight: 1.1,
      letterSpacing: '-0.025em',
      wordSpacing: 'normal',
      color: '#021B1B',
      fontFamily: APP_FONT},

    // H2 — Headline Medium (M3 role). Phase C: Layout route / major section title.
    h2: {
      fontSize: '28px',
      fontWeight: 700,
      lineHeight: 1.2,
      letterSpacing: '-0.015em',
      wordSpacing: 'normal',
      textTransform: 'none',
      color: '#021B1B',
      fontFamily: APP_FONT},

    // H3 — Title Large (M3 role). Phase C: subsection / drawer + modal chrome titles.
    h3: {
      fontSize: '22px',
      fontWeight: 600,
      lineHeight: 1.3,
      letterSpacing: TYPOGRAPHY_H3_LETTER_SPACING,
      wordSpacing: 'normal',
      textTransform: 'none',
      color: '#021B1B',
      fontFamily: APP_FONT},

    /**
     * @deprecated Prefer `typography.h3.letterSpacing` — same value, kept for call sites that still reference it.
     */
    titleLetterSpacing: TYPOGRAPHY_H3_LETTER_SPACING,

    // H4 — Title Small / card titles (M3 role map). Phase C: 18 / 600.
    h4: {
      fontSize: '18px',
      fontWeight: 600,
      lineHeight: 1.4,
      letterSpacing: 'normal',
      wordSpacing: 'normal',
      color: '#021B1B',
      fontFamily: APP_FONT},

    /**
     * **Sectional header** titles on bordered card blocks — not the generic `h4` card title.
     * Maps to M3 Title Medium. Phase C: 16 / 600 / 1.4.
     *
     * **When to use:** Subsection headings inside outlines that match staff form shells
     * (`colors.border.light` / structural grey): drawer form sections (`components.drawerForm.section`),
     * multi-section editors, and bordered marketing/choice cards where the title is a section
     * under the page hero. Pairs with {@link components.drawerForm.section} in drawers.
     *
     * **When to use `h4` instead:** Standalone card titles, modal headers, empty states, and
     * headlines that are not “one block among several” inside the same bordered card stack.
     *
     * Prefer `IOSTypography` `variant="h4"` + `style={{ ...theme.typography.formCardSectionTitle }}`
     * (or equivalent) and set `component` for document outline.
     *
     * @see docs/design-system/TEBOX_DESIGN_SYSTEM.html#typography-sectional-headers
     */
    formCardSectionTitle: {
      fontSize: '16px',
      fontWeight: 600,
      lineHeight: 1.4,
      letterSpacing: 'normal',
      wordSpacing: 'normal',
      color: '#021B1B',
      fontFamily: APP_FONT},

    // Body - Standard body text, paragraphs, primary content
    // Also used for: subtitles, buttons, form field values, table body, underline nav tab labels (components.tabs.tab matches this scale)
    body: {
      fontSize: '13px',
      fontWeight: 400,
      lineHeight: 1.5,
      letterSpacing: 'normal',
      color: '#021B1B',
      fontFamily: APP_FONT},

    // Body Small - Smaller body text, secondary content
    bodySmall: {
      fontSize: '12px',
      fontWeight: 400,
      lineHeight: 1.5,
      letterSpacing: 'normal',
      color: '#021B1B',
      fontFamily: APP_FONT},

    // Caption - Labels, helper text, form labels, table micro-copy (below bodySmall)
    caption: {
      fontSize: '11px',
      fontWeight: 300,
      lineHeight: 1.4,
      letterSpacing: '0.04em',
      color: '#021B1B',
      fontFamily: APP_FONT},

    /**
     * Block label above a control (`FieldLabel`, `FormInfieldShell`).
     * Caption scale (11px / 1.4), medium. Pairs with `colors.border.field` ({@link BORDER_FIELD}).
     */
    fieldLabelBlock: {
      fontSize: '11px',
      fontWeight: 500,
      lineHeight: 1.4,
      letterSpacing: '0.04em',
      color: FORM_FIELD_LABEL_HEX,
      fontFamily: APP_FONT},

    /**
     * Dense label above bordered form groups (caption scale).
     * For drawer card titles at h4 scale, use {@link formCardSectionTitle} instead.
     */
    formSectionLabel: {
      fontSize: '11px',
      fontWeight: 500,
      lineHeight: 1.4,
      letterSpacing: '0.05em',
      textTransform: 'none' as const,
      color: '#021B1B',
      fontFamily: APP_FONT},

    /**
     * Numeric scale — currency, counts, KPI figures. Inter proportional figures.
     * CSS mirrors: `.numeric-*` in `apps/web/src/index.css`. Prefer `<Money>` for currency.
     */
    numeric: {
      display: {
        fontSize: '32px',
        fontWeight: 500,
        lineHeight: 1.1,
        letterSpacing: '-0.02em',
        fontFamily: APP_FONT,
      },
      headline: {
        fontSize: '22px',
        fontWeight: 500,
        lineHeight: 1.2,
        letterSpacing: '-0.01em',
        fontFamily: APP_FONT,
      },
      body: {
        fontSize: '13px',
        fontWeight: 400,
        lineHeight: 1.5,
        letterSpacing: 'normal',
        fontFamily: APP_FONT,
      },
      compact: {
        fontSize: '12px',
        fontWeight: 500,
        lineHeight: 1.4,
        letterSpacing: 'normal',
        fontFamily: APP_FONT,
      },
    },

    /**
     * DEPRECATED TOKENS - DO NOT USE IN NEW CODE
     * These are kept for backward compatibility during migration.
     * Map to the 7 core tokens above:
     * - h5 → Use h4
     * - h6 → Use h4  
     * - modalHeader → Use h4
     * - body1 → Use body
     * - body2 → Use bodySmall
     * - subtitle1 → Use body
     * - subtitle2 → Use bodySmall
     * - tinyText → Use caption
     * - button → Use bodySmall (12px) with fontWeight: 500 override
     * - formLabel → Use caption
     * - fieldLabel → Use fieldLabelBlock (or caption + regular weight)
     * - tableData → Use caption
     */
    
    // @deprecated Use h4 instead
    h5: {
      fontSize: '20px',
      fontWeight: 500,
      lineHeight: '31.5px',
      letterSpacing: 'normal',
      wordSpacing: 'normal',
      color: '#021B1B',
      fontFamily: APP_FONT},

    // @deprecated Use h4 instead
    h6: {
      fontSize: '20px',
      fontWeight: 500,
      lineHeight: '31.5px',
      letterSpacing: 'normal',
      wordSpacing: 'normal',
      color: '#021B1B',
      fontFamily: APP_FONT},

    // @deprecated Use h4 instead
    modalHeader: {
      fontSize: '20px',
      fontWeight: 500,
      lineHeight: '31.5px',
      letterSpacing: 'normal',
      wordSpacing: 'normal',
      color: '#021B1B',
      fontFamily: APP_FONT},

    // @deprecated Use body instead
    body1: {
      fontSize: '13px',
      fontWeight: 400,
      lineHeight: 1.5,
      letterSpacing: 'normal',
      color: '#021B1B',
      fontFamily: APP_FONT},
    
    // @deprecated Use bodySmall instead
    body2: {
      fontSize: '12px',
      fontWeight: 400,
      lineHeight: 1.5,
      letterSpacing: 'normal',
      color: '#021B1B',
      fontFamily: APP_FONT},
    
    // @deprecated Use body instead
    subtitle1: {
      fontSize: '13px',
      fontWeight: 400,
      lineHeight: 1.5,
      letterSpacing: 'normal',
      color: '#021B1B',
      fontFamily: APP_FONT},
    
    // @deprecated Use bodySmall instead
    subtitle2: {
      fontSize: '12px',
      fontWeight: 400,
      lineHeight: 1.5,
      letterSpacing: 'normal',
      color: '#021B1B',
      fontFamily: APP_FONT},
    
    // @deprecated Use caption instead
    tinyText: {
      fontSize: '11px',
      fontWeight: 500,
      lineHeight: 1.4,
      letterSpacing: '0.04em',
      color: '#021B1B',
      fontFamily: APP_FONT},
    
    // @deprecated Use body or bodySmall instead
    button: {
      fontSize: '13px',
      fontWeight: 400,
      lineHeight: 1.5,
      letterSpacing: 'normal',
      color: '#021B1B',
      fontFamily: APP_FONT},
    
    // @deprecated Use caption instead
    formLabel: {
      fontSize: '11px',
      fontWeight: 500,
      lineHeight: 1.4,
      letterSpacing: '0.04em',
      color: '#021B1B',
      fontFamily: APP_FONT},
    
    // @deprecated Use caption instead (with fontWeight: 500 override if needed)
    fieldLabel: {
      fontSize: '11px',
      fontWeight: 500,
      lineHeight: 1.4,
      letterSpacing: '0.04em',
      color: '#021B1B',
      fontFamily: APP_FONT},
    
    // @deprecated Use caption instead
    tableData: {
      fontSize: '11px',
      fontWeight: 500,
      lineHeight: 1.4,
      letterSpacing: '0.04em',
      color: '#021B1B',
      fontFamily: APP_FONT},

    /**
     * Gray subline under main layout / page titles (Layout + matching in-page headers).
     * Documented in docs/design-system/TEBOX_DESIGN_SYSTEM.html (Page header tokens).
     */
    pageHeaderSubtext: {
      fontSize: '13px',
      fontWeight: 400,
      lineHeight: 1.55,
      letterSpacing: 'normal',
      /** Matches `colors.text.secondary` — avoids washed-out rgba on white (Layout + page chrome). */
      color: FORM_FIELD_LABEL_HEX,
      fontFamily: APP_FONT}},
  borderRadius: {
    /** Tables, dropdown menus, checkboxes — sharp corners. */
    none: '0px',
    /** Soft chrome alias — used by LandingPageCard, SearchBar, SummaryMetricTile, etc. */
    xs: RADIUS_CONTAINER,
    /** Soft chrome alias (TEBOX `--radius-sm` / `--radius-container`). */
    sm: RADIUS_CONTAINER,
    /** Form fields / MUI outlined (TEBOX `--radius-field`). */
    field: RADIUS_CONTAINER,
    /** Cards, panels, drawers, table shells (outer). */
    card: RADIUS_CONTAINER,
    /** Buttons / primary actions. */
    button: RADIUS_CONTAINER,
    /** Tee-sheet player cells. */
    teeSheetCell: RADIUS_CONTAINER,
    /** Canonical soft-chrome token — prefer this or `card` for new soft surfaces. */
    container: RADIUS_CONTAINER,
    /** Aliases of soft chrome — do not invent mid-radii (8/12/16px). */
    md: RADIUS_CONTAINER,
    lg: RADIUS_CONTAINER,
    xl: RADIUS_CONTAINER,
    /** Avatars and circular icon controls only. */
    full: RADIUS_FULL,
    /** Marketing landing product screenshots (`LandingPage` ProductSection). */
    productScreenshot: '10px'},
  shadows: {
    sm: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
    md: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
    lg: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
    xl: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
    '2xl': '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
    /** Marketing landing product screenshots (`LandingPage` ProductSection) — frame. */
    productScreenshot: '7.1px 7.4px 12.2px -1.3px rgba(0, 0, 0, 0.09)',
    /** Marketing landing product screenshots — image lift. */
    productScreenshotImage: '0px 4px 12px 0px rgba(0, 0, 0, 0.15)'},
  // Component-specific design tokens for backward compatibility
  components: {
    /** Title + subtext + primary actions live in app Layout (`Layout.tsx`) only — never duplicate in page body. See TEBOX § layout-main-header-no-duplicate. */
    layoutHeader: {
      /** Vertical padding for the main layout header bar (`Layout.tsx` title + actions row). */
      chromePaddingBlock: '30px',
      /** Horizontal padding for the main layout header bar — same as page `contentGutter`. */
      chromePaddingInline: CONTENT_GUTTER,
      titleBlockPadding: `${SPACING_PRIMITIVES.xs} ${SPACING_PRIMITIVES.sm}`,
      titleToSubtextGap: SPACING_PRIMITIVES.sm,
      titleStackLineHeight: 1.12,
      /**
       * Routes registered in `config/routes.ts` (`title`, `subtitle`) render in Layout.
       * Page body must not repeat route title, subtitle, or the primary header action button.
       * Register create actions via `window.handleCreate*` + `Layout.getPageConfig()` (see Tax Classes, Lifecycle stages).
       */
      noDuplicateInPageBody: ['routeTitle', 'routeSubtitle', 'primaryHeaderAction'] as const,
    },

    /**
     * Outline SVG icons — stroke width in **user units** (viewBox 20×20 / 24×24).
     * Matches TEBOX module tiles + `IOSIcon` default (1.5). Booking app mirrors via `icon-stroke.ts`.
     */
    icon: {
      strokeStandard: 1.5,
      strokeHairline: 1,
      strokeEmphasis: 2,
      /** Golf cart glyph (20×20) — authored at stroke 2; 1.5 reads too light at tee-sheet / KPI sizes. */
      strokeGolfCart: 2,
    },

    /**
     * Entity activity timeline — reservation drawer, gift card drawer, etc.
     * Narrative rows: colored icon circles + continuous vertical line + bold/gray text + relative time.
     */
    activityTimeline: {
      iconColumnWidth: 28,
      iconSize: 28,
      iconGlyphSize: 14,
      iconBorderWidth: 2,
      lineWidth: 2,
      lineTop: 14,
      lineBottom: 14,
      itemGap: 12,
      itemPaddingBottom: 20,
      narrativeLineHeight: 1.5,
      sectionTitleMarginBottom: 8,
      subcaptionMarginTop: 4,
      memoMarginTop: 4,
      accessoryMarginTop: 4,
      timestampMarginTop: 4,
    },

    /**
     * Guest booking results grid card (`TeeTimeCard` in `@boubou/booking`).
     * Staff Brand settings BookingPreview mirrors this shell.
     */
    /**
     * Tee sheet grid row — time column label (`TeeTimeRow`, inline `HH:mm`).
     * Expanded mode is larger than compact; `:00` rows use `fontWeightHourMark` for scan anchors.
     */
    teeSheetRow: {
      timeInterval: {
        expanded: {
          fontSize: '18px',
          fontWeightHourMark: 900,
          fontWeightInterval: 600,
        },
        compact: {
          fontSize: '18px',
          fontWeightHourMark: 900,
          fontWeightInterval: 500,
        },
        clickPadding: '2px',
      },
      /** Expanded tee row player name (`PlayerCell`); also sidebar nav labels in `IOSSidebar`. */
      playerName: {
        fontFamily: APP_FONT,
        fontSize: '16px',
        fontWeight: 500,
        lineHeight: '21px',
        letterSpacing: '0px',
      },
    },

    teeTimeCard: {
      /** Scale vertical padding/margins (~20% shorter card). Use `calc(token * verticalCompactScale)`. */
      verticalCompactScale: 0.8,
      cardHeight: '118px',
      cardMinHeight: '138px',
      demandTrendCircleDiameterPx: 24,
      cardVerticalPadding: '16px',
      courseNameFontSize: '16px',
      clockFontSize: '26px',
      heroHeaderNumericFontSize: '26px',
      pricingCardCourseTitleFontSize: '24px',
      /** Matches `typography.h4.fontSize`. */
      reservationCardCourseTitleFontSize: '20px',
      reservationCardCourseTitleFontWeight: 500,
    },

    /**
     * Shared `EmptyState` (dashed panel, icon + title + body).
     * Surface: `colors.background.secondary` (#FAFAF9) — same hex as default row hover; dashed border + layout distinguish empty panels from data rows.
     * Implementation: `apps/web/src/components/EmptyState.tsx`
     */
    emptyState: {
      /**
       * Opacity for the icon + title + description stack. Product default: two successive ~20% transparency
       * passes (0.8 × 0.8). Optional `action` is rendered outside this group at full opacity.
       */
      foregroundContentOpacity: 0.8 * 0.8,
      /** Optional layered `backgroundIcon` glyph behind the primary icon */
      backgroundIconOpacity: 0.22,
    },

    /**
     * StandardTableV2 — cell padding and optional row density (see #spacing-table-density in TEBOX_DESIGN_SYSTEM.html).
     */
    standardTable: {
      contentGutter: CONTENT_GUTTER,
      density: STANDARD_TABLE_DENSITY,
      /** `<thead>` column titles — medium (500). */
      headerFontWeight: 500,
      /** Primary row label / name in body cells (first column, CustomerTableCell name line). */
      rowNameFontWeight: 500,
    },

    /**
     * Above-table totals (`TableTotalDisplay`) and `<Money size="tableTotal">`.
     * Integer digits use the headline scale; fractional digits render at 75% size (baseline-aligned).
     */
    tableTotalDisplay: {
      defaultAmountColor: '#000000',
      sizes: {
        lg: {
          integer: TABLE_TOTAL_LG_AMOUNT,
          decimal: TABLE_TOTAL_LG_DECIMAL,
        },
        md: {
          integer: TABLE_TOTAL_MD_AMOUNT,
          decimal: TABLE_TOTAL_MD_DECIMAL,
        },
        sm: {
          integer: TABLE_TOTAL_SM_AMOUNT,
          decimal: TABLE_TOTAL_SM_DECIMAL,
        },
      },
    },

    /**
     * TableKPICard — summary metrics above reporting / table views.
     * Surface: colors.background.secondary (#FAFAF9). Primary metric uses text.primary, not accent green.
     */
    tableKpiCard: {
      container: {
        padding: CONTENT_GUTTER,
        paddingBottomWithIcon: '28px',
        minHeight: '117px'},
      titleToDescriptionGap: MICRO_GAP,
      /** Matches spacing.lg — separates metric block from title / description */
      descriptionToMetricGap: SPACING_PRIMITIVES.lg,
      valueLabelToValueGap: '2px',
      title: {
        fontSize: '16px',
        fontWeight: 500,
        lineHeight: 1.25},
      description: {
        fontSize: '13px',
        fontWeight: 400,
        lineHeight: 1.45},
      valueLabel: {
        fontSize: '11px',
        fontWeight: 500,
        lineHeight: 1.2,
        letterSpacing: 'normal',
        textTransform: 'none' as const},
      value: TABLE_KPI_VALUE,
      valueDecimal: TABLE_KPI_VALUE_DECIMAL,
      valueRow: {
        gap: MICRO_GAP,
        lineHeight: 1.1},
      valueSuffix: {
        fontSize: '13px',
        fontWeight: 400},
      /** Optional growth / “New” pill (top-right, overlaps corner) */
      growthPill: {
        fontSize: '10px',
        fontWeight: 500,
        color: '#422006',
        backgroundColor: '#FEF9C3',
        border: '1px solid #FDE047',
        borderRadius: RADIUS_CONTAINER,
        padding: '3px 10px',
        lineHeight: 1.2},
      growthPillPosition: {
        top: 0,
        right: 0,
        transform: 'translate(12%, -28%)'},
      iconSlot: {
        width: '16px',
        height: '16px',
        borderRadius: RADIUS_CONTAINER}},

    /**
     * Customer profile accent KPI tile — Overview Quick stats, Wallet, Membership, Gift Cards tabs.
     * Value-first at body scale with `kpiStrip.accents` wash (distinct from reporting `KpiStrip` 28px tiles).
     */
    customerProfileKpiTile: {
      padding: SPACING_PRIMITIVES.md,
      valueFontWeight: 500,
      valueTypography: {
        fontFamily: APP_FONT,
        fontSize: '13px',
        fontWeight: 500,
        lineHeight: 1.35,
        letterSpacing: 'normal',
        fontVariantNumeric: 'tabular-nums' as const,
      },
    },

    /**
     * `KpiStrip` (`apps/web/src/components/design-system/KpiStrip.tsx`).
     * `plain`: column gap only; `bordered`: single hairline outer shell.
     * `panel` (default): `TableKPICard`-aligned grid — secondary surface tiles, value on top,
     * title + description below (`tableKpiCard` tokens). Used on ledger, deferred revenue, and reporting KPI rows.
     */
    kpiStrip: {
      /** Horizontal gap between metric columns (`grid-column-gap`) — plain & bordered only. */
      columnGap: SPACING_PRIMITIVES['3xl'],
      /** Plain / panel variant — outer vertical padding (`padding-block`). */
      plainPaddingBlock: '30px',
      /** Panel — soft chrome on reporting KPI tiles. */
      panelBorderRadius: RADIUS_CONTAINER,
      /** Panel — horizontal inset inside each column. */
      panelCellPaddingInline: SPACING_PRIMITIVES.xl,
      /** Panel — spacing under title row to subtitle/count line. */
      panelTitleToSubtitleGap: SPACING_PRIMITIVES.xs,
      /** Panel — subtitle to primary value stack. */
      panelSubtitleToValueGap: SPACING_PRIMITIVES.sm,
      /** Panel — gap between primary amount and trailing trend cluster. */
      panelValueTrendGap: SPACING_PRIMITIVES.md,
      /** Panel — fixed height on the primary value row (tightens tile vertical rhythm). */
      panelValueRowHeight: '10px',
      /** Panel — baseline gap inside trend chip (icons / % / comparison text). */
      panelTrendClusterGap: SPACING_PRIMITIVES.xs,
      /** Panel — circular hit target for overflow menu (`minWidth`/`minHeight`). */
      panelMenuButtonSize: SPACING_PRIMITIVES['2xl'],
      /** Bordered variant — inner cell padding (`2×` the prior `20px` / `24px`). */
      borderedCellPaddingBlock: '40px',
      borderedCellPaddingInline: '48px',
      /** Space between caption row and primary value (tight ramp like `TableTotalDisplay` label→amount). */
      labelToValueGap: SPACING_PRIMITIVES.xs,
      /** Space between primary value and subtext / meta line (same tight stack as label→value). */
      valueToSubtextGap: SPACING_PRIMITIVES.xs,
      /** Space above the delta chip row. */
      deltaChipMarginTop: '12px',
      /**
       * Panel tile accent washes — light fills + matching hairline borders.
       * Values stay `text.primary`; colour is surface-only (Ramp / Tebox reporting rhythm).
       */
      accentCycle: ['lime', 'sky', 'mint', 'amber', 'violet', 'rose'] as const,
      accents: {
        lime: {
          background: '#FAFFF0',
          border: '#F0FAD6',
          hoverBackground: '#F5FFE8',
        },
        sky: {
          background: '#F8FBFF',
          border: '#EDF4FF',
          hoverBackground: '#F2F7FF',
        },
        mint: {
          background: '#F7FDFA',
          border: '#ECFAF2',
          hoverBackground: '#F2FCF6',
        },
        amber: {
          background: '#FFFCF5',
          border: '#FFF6E5',
          hoverBackground: '#FFF9ED',
        },
        violet: {
          background: '#FAFAFF',
          border: '#F2F0FF',
          hoverBackground: '#F5F4FF',
        },
        rose: {
          background: '#FFFAFB',
          border: '#FFF0F2',
          hoverBackground: '#FFF5F6',
        },
        neutral: {
          background: BACKGROUND_SURFACE,
          border: BORDER_FIELD,
          hoverBackground: '#FFFFFF',
        },
      },
    },

    /**
     * Dynamic pricing list tiles — status accent washes as soft diagonal gradients.
     * Implementation: `policyTileSurface` in `dynamicPricingListTable.ts`.
     */
    dynamicPricingPolicyTile: {
      gradientAngleDeg: 165,
      /** Mid-stop mix of accent border into `colors.background.primary`. */
      gradientMidMixPercent: 32,
      hoverGradientMidMixPercent: 40,
      /** Accent border mixed into `colors.border.dark` for a clearer card edge. */
      borderAccentMixPercent: 52,
      hoverBorderAccentMixPercent: 62,
    },

    /** Horizontal rule-tree canvas — editor background + connectors. */
    dynamicPricingRuleCanvas: {
      backgroundColor: BACKGROUND_SIDEBAR_BEIGE,
      dotColor: BORDER_FIELD,
      dotSpacingPx: 20,
      connectorColor: '#86888C',
      /** Right inspector when a node is selected (was 360px — too narrow for time/occupancy fields). */
      inspectorWidthPx: 480,
    },

    /** Apply-template drawer — course + schedule assignment (wider than default 675px staff drawer). */
    dynamicPricingApplyDrawer: {
      width: 'min(960px, 92vw)',
    },

    /** Schedule Builder — day detail drawer (remove/split assignments; narrower than apply drawer). */
    scheduleBuilderDayDrawer: {
      width: 'min(520px, 92vw)',
    },

    /** Schedule Builder — draggable template catalog rail beside the calendar. */
    scheduleBuilderCatalogRail: {
      expandedMaxWidthPx: 280,
      collapsedWidthPx: 40,
    },

    /**
     * Table header filter bar — underline controls above `StandardTableV2` (reporting / ledger).
     * `TableHeaderFilterBar` + `TableHeaderFilterField` (`UnderlineFilterShell`).
     * Preset: `ReportingFilterBar` (search + slice + date). See #components-table-header-filter-bar.
     */
    /** Popover list shell + rows — see `TableHeaderFilterMenu` / `TeboxSelectDropdown`. */
    dropdownMenu: DROPDOWN_MENU_TOKENS,

    tableHeaderFilterBar: {
      bar: {
        alignItems: 'flex-end',
        gap: SPACING_PRIMITIVES.lg,
        marginBottom: SPACING_PRIMITIVES.lg,
      },
      field: {
        paddingTop: SPACING_PRIMITIVES.xs,
        paddingBottom: '10px',
        labelMarginBottom: '6px',
        valueRowMinHeight: '28px',
        valueRowGap: SPACING_PRIMITIVES.sm,
        valueFontWeight: 500,
      },
      menu: DROPDOWN_MENU_TOKENS,
      clearButton: {
        marginBottom: '6px',
        minWidth: '72px',
      },
      /** Search field is slightly wider than slice/date columns on md+ layouts. */
      searchFieldFlex: 1.15,
      defaultFieldFlex: 1,
      searchMinWidthMd: '220px',
      filterMinWidthMd: '180px',
    },

    /**
     * Golf cart glyph (20×20 viewBox) — pixel widths for `CartsIcon` and related `size` props.
     * Kept minimal: only slots referenced by Header, TableKPICard, and TeeSheetKPIStrip.
     */
    carIcon: {
      /** Icon width inside TableKPICard corner chip (`cloneElement` override) */
      kpiCardCornerGlyph: 9,
      /** Header “Carts reserved” tile */
      headerKpi: 15,
      /** Tee sheet KPI popover rows */
      kpiPopover: 13},

    /**
     * Financial / reporting table headers (EOD, ledger, deferred revenue, aging, pass liability).
     * Body-scale labels on primary surface; tabular numerals keep currency column titles aligned with amounts.
     * Documented in docs/design-system/TEBOX_DESIGN_SYSTEM.html (#components-financial-table-header).
     */
    financialTableHeader: {
      /** Muted header band — `BACKGROUND_SURFACE` / `colors.background.secondary` (vs white tbody). */
      backgroundColor: BACKGROUND_SURFACE,
      /** Matches `colors.table.rowDivider` (lighter than structural `colors.border.light`). */
      borderBottom: `1px solid ${TABLE_GRID_DIVIDER_HEX}`,
      /** First column (row label); matches colors.table headerCellPaddingFirst */
      paddingFirstColumn: STANDARD_TABLE_DENSITY.comfortable.paddingHeaderFirst,
      /** Amount / date / numeric columns */
      paddingDefault: STANDARD_TABLE_DENSITY.comfortable.paddingHeaderRest,
      /** Header label typography — same as `typography.body` (13px) + 500; use with StandardTableHeaderCellV2 or raw <th> */
      label: {
        fontFamily: APP_FONT,
        fontSize: '13px',
        fontWeight: 500,
        lineHeight: 1.4,
        letterSpacing: 'normal',
        textTransform: 'none' as const,
        color: '#021B1B',
        fontVariantNumeric: 'tabular-nums' as const,
      },
    },

    /**
     * Settings list pages — Online Booking, Season goals, etc.
     * See: docs/design-system/TEBOX_DESIGN_SYSTEM.html (#components-settings-panel)
     */
    settingsPanel: {
      /** Staff Settings page (`Settings.tsx`) left nav rail — horizontal inset inside the rail. */
      navRailPaddingInline: SPACING_PRIMITIVES.md,
      /** Settings nav rail — leading (left) inset; tighter than trailing for icon alignment. */
      navRailPaddingInlineStart: '5px',
      /** Group headers in the settings nav rail (e.g. "Pricing") — reuse on Schedule Builder catalog lanes. */
      navSectionTitle: {
        fontFamily: APP_FONT,
        fontSize: PILL_FONT_SIZE,
        fontWeight: 500,
        lineHeight: 1.2,
        letterSpacing: '0.5px',
        color: '#021B1B',
        backgroundColor: 'unset',
        padding: `${SPACING_PRIMITIVES.md} ${SPACING_PRIMITIVES.sm} ${SPACING_PRIMITIVES.xs} ${SPACING_PRIMITIVES.sm}`,
      },
      pagePadding: SPACING_PRIMITIVES.lg,
      /** Gap between stacked `SettingsPanelCard`s on list pages. */
      stackGap: SPACING_PRIMITIVES.lg,
      /** Max readable width for Online Booking–style forms inside settings main. */
      contentMaxWidth: '720px',
      /** Horizontal gap between row label block and trailing control. */
      rowGap: SPACING_PRIMITIVES.md,
      titleToDescriptionGap: SPACING_PRIMITIVES.xs,
      card: {
        border: `1px solid ${BORDER_FIELD}`,
        borderRadius: RADIUS_CONTAINER,
        backgroundColor: '#FFFFFF',
        marginTop: SPACING_PRIMITIVES.lg,
        paddingInline: CONTENT_GUTTER,
        paddingBlock: SPACING_PRIMITIVES.md,
        paddingBlockDense: SPACING_PRIMITIVES.md},
      sectionRow: {
        paddingBlock: SPACING_PRIMITIVES.md,
        borderBottom: `1px solid ${TABLE_GRID_DIVIDER_HEX}`},
      /** Row title — same scale as `typography.fieldLabelBlock`, primary ink. */
      title: {
        fontFamily: APP_FONT,
        fontWeight: 500,
        fontSize: '11px',
        lineHeight: 1.4,
        letterSpacing: '0.04em',
        color: '#021B1B'},
      /** Row / card helper — same scale as `typography.caption`, secondary ink. */
      description: {
        fontFamily: APP_FONT,
        fontWeight: 300,
        fontSize: '11px',
        lineHeight: 1.4,
        letterSpacing: '0.04em',
        color: FORM_FIELD_LABEL_HEX},
      /** Layout chrome for URL readouts; use `typography.bodySmall` for the URL string. */
      urlReadout: {
        marginTop: SPACING_PRIMITIVES.md,
        padding: `${SPACING_PRIMITIVES.sm} ${SPACING_PRIMITIVES.md}`,
        backgroundColor: BACKGROUND_SURFACE,
        border: `1px solid ${BORDER_FIELD}`,
        borderRadius: RADIUS_CONTAINER,
        lineHeight: 1.45,
        wordBreak: 'break-all' as const},
      /** Toggle stacked above numeric field on Season goals–style rows */
      controlStackGap: SPACING_PRIMITIVES.sm,
      inlineMessageSuccess: '#29BF12',
      inlineMessageError: '#dc2626'},

    /**
     * SimpleDatePicker trigger — matches teboxTextField.container.border (not borderless).
     * Implementation: apps/web/src/components/design-system/date-pickers/SimpleDatePicker.tsx
     */
    simpleDatePicker: {
      defaultInputHeight: MATERIAL_OUTLINED_MIN_HEIGHT,
      usesTeboxTextFieldBorder: true,
      /** Open calendar panel — menu/panel stays sharp (`borderRadius.none`). */
      dropdownBorderRadius: '0px'},

    /** Criterion toggles + `MultiSelectDropdown` row checkbox — fixed box size + sharp corners. */
    formCheckbox: {
      size: '18px',
      boxBorderRadius: '0px',
      uncheckedBackgroundColor: '#FFFFFF',
      uncheckedBorderColor: BORDER_FIELD,
      checkedBackgroundColor: '#B4CC16',
      checkmarkImage: FORM_CHECK_ICON_DATA_URL,
      checkBackgroundSize: 'calc(100% - 4px)',
      focusOutline: '2px solid #B4CC16',
      focusOutlineOffset: '2px',
      labelAlignMarginTop: '3px',
    },

    /** Preset + custom swatch pickers (`ColorPicker`, `CompactColorPicker`). */
    colorPicker: {
      swatchSize: '28px',
      swatchSizeCompact: '24px',
      previewSize: '40px',
      gridColumns: 6,
      gridGap: MICRO_GAP,
      panelPadding: SPACING_PRIMITIVES.sm,
      sectionGap: SPACING_PRIMITIVES.sm,
    },

    /**
     * Day-of-week chip row (pricing templates, tee time rules, recurring memos, etc.).
     * Selected: soft gray fill + medium gray border; unselected: white + structural border, muted label.
     * See: apps/web/src/pages/settings/PricingSchemaForm.tsx
     */
    dayOfWeekToggle: {
      row: {
        display: 'grid',
        width: '100%',
        gridTemplateColumns: 'repeat(7, minmax(0, 1fr))',
        gap: SPACING_PRIMITIVES.sm},
      button: {
        width: '100%',
        minWidth: 0,
        padding: `${SPACING_PRIMITIVES.sm} 10px`,
        fontSize: '13px',
        fontWeight: 500,
        fontFamily: APP_FONT,
        borderRadius: RADIUS_CONTAINER,
        cursor: 'pointer',
        transition: 'all 0.2s ease',
        textTransform: 'none' as const,
        textAlign: 'center' as const,
        boxSizing: 'border-box' as const},
      selected: {
        backgroundColor: '#F3F4F6',
        color: '#021B1B',
        border: '1px solid #9CA3AF'},
      unselected: {
        backgroundColor: '#FFFFFF',
        color: FORM_FIELD_LABEL_HEX,
        border: `1px solid ${BORDER_FIELD}`},
      /** Hover surface when chip is not selected */
      hoverUnselectedBackground: BACKGROUND_SURFACE},

    /**
     * Roomy MUI outlined shell (floating label + default OutlinedInput in theme).
     * `MaterialOutlinedTextField`, `SimpleDatePicker` `materialOutlinedPilot`, and MUI `components.MuiOutlinedInput` in `theme.ts`.
     */
    materialOutlinedField: {
      minHeight: MATERIAL_OUTLINED_MIN_HEIGHT,
      paddingY: MATERIAL_OUTLINED_PADDING_Y,
      paddingX: MATERIAL_OUTLINED_PADDING_X,
      selectIconGutter: MATERIAL_OUTLINED_SELECT_ICON_GUTTER,
      menuPaperMaxHeight: 320},

    formInput: {
      base: {
        width: '100%',
        height: MATERIAL_OUTLINED_MIN_HEIGHT,
        padding: `${MATERIAL_OUTLINED_PADDING_Y} ${MATERIAL_OUTLINED_PADDING_X}`,
        boxSizing: 'border-box',
        border: `1px solid ${BORDER_FIELD}`,
        borderRadius: RADIUS_CONTAINER,
        // typography.body — form values align with table / page body
        fontSize: '13px',
        lineHeight: 1.5,
        fontFamily: APP_FONT,
        backgroundColor: BACKGROUND_SURFACE,
        color: '#021B1B',
        disabledColor: '#86888C',
        outline: 'none',
        transition: 'border-color 0.2s ease'
      },
      focus: {
        borderColor: '#B4CC16' // Primary Darker – focus highlight
      },
      hover: {
        borderColor: BORDER_FIELD
      }
    },
    /**
     * FORM FIELD COMPONENTS (PREFERRED)
     *
     * Use FormField + FormInput / FormSelect / FormTextArea for all form inputs.
     * Native HTML elements properly fill their containers (unlike MUI TextField).
     *
     * - FormInput: text, email, tel, url, etc.
     * - FormSelect: author <option> children; UI is TeboxSelectDropdown (portaled Tebox menu, not OS select)
     * - FormTextArea: multiline text
     *
     * Tokens below (teboxTextField) drive FormInput/FormSelect/FormTextArea styling.
     * Metrics align with `components.materialOutlinedField` (roomy outlined family).
     * @deprecated TeboxTextField (MUI) - use FormField + FormInput/FormSelect/FormTextArea or MaterialOutlinedTextField
     */
    teboxTextField: {
      label: {
        fontSize: '11px',
        fontWeight: 500,
        color: '#646870', // colors.text.tertiary
        fontFamily: APP_FONT,
        position: 'absolute',
        top: '2px',
        left: MATERIAL_OUTLINED_PADDING_X,
        transform: 'none', // Label stays in place, no animation
        pointerEvents: 'none',
        requiredAsterisk: {
          marginLeft: '2px',
          color: '#646870'
        }
      },
      input: {
        // typography.body — field value text (size, line-height, weight match `typography.body`)
        fontSize: '13px',
        lineHeight: 1.5,
        letterSpacing: 'normal',
        fontFamily: APP_FONT,
        fontWeight: 400,
        color: '#021B1B',
        paddingTop: MATERIAL_OUTLINED_PADDING_Y,
        paddingBottom: MATERIAL_OUTLINED_PADDING_Y,
        paddingLeft: MATERIAL_OUTLINED_PADDING_X,
        paddingRight: MATERIAL_OUTLINED_PADDING_X,
        /** Disabled value ink — matches `colors.text.disabled`. */
        disabledColor: '#86888C',
        placeholder: {
          opacity: 0.45,
          color: '#000000'
        }
      },
      container: {
        height: MATERIAL_OUTLINED_MIN_HEIGHT,
        minHeight: MATERIAL_OUTLINED_MIN_HEIGHT,
        backgroundColor: '#FFFFFF',
        borderRadius: RADIUS_CONTAINER,
        border: `1px solid ${BORDER_FIELD}`,
        borderHover: BORDER_FIELD,
        borderFocus: '#B4CC16', // Primary Darker – focus highlight
        borderFocusWidth: '2px',
        borderError: '#EF4444', // colors.status.error
        borderErrorWidth: '2px',
        borderDisabled: BORDER_FIELD,
        backgroundColorDisabled: BACKGROUND_SURFACE,
        disabledBackgroundColor: BACKGROUND_SURFACE // Used when input is disabled (FormInput/FormSelect/FormTextArea)
      },
      multiline: {
        minHeight: 'unset', // Allows auto-expansion based on rows prop
        paddingTop: MATERIAL_OUTLINED_PADDING_Y,
        paddingBottom: MATERIAL_OUTLINED_MULTILINE_PADDING_BOTTOM,
        paddingLeft: MATERIAL_OUTLINED_PADDING_X,
        paddingRight: MATERIAL_OUTLINED_PADDING_X,
        lineHeight: '1.5',
        resize: 'none', // Controlled by rows prop
        overflow: 'hidden', // Controlled by rows prop
        color: '#021B1B',
        disabledColor: '#86888C',
      }
    },
    /**
     * SELECT / DROPDOWN (admin web)
     *
     * - Forms: `FormField` + `FormSelect` → `TeboxSelectDropdown` (`usePortal` default true)
     * - Menus: `TableHeaderFilterMenuPanel*` + `components.dropdownMenu` tokens
     * - Filters: `TableHeaderFilterBar`; multi: `MultiSelectDropdown`; compact: `TableDropdown`
     * - Modals: `portalZIndex` (1400); reservation: `RESERVATION_MODAL_OVERLAY_Z`
     * - Legacy only: `IOSSelect` — do not use on new admin screens
     *
     * See `docs/design-system/TEBOX_DESIGN_SYSTEM.html` → Dropdown menu & selects.
     */
    formSelect: {
      base: {
        height: MATERIAL_OUTLINED_MIN_HEIGHT,
        // Body Small
        fontSize: '12px',
        fontFamily: APP_FONT
      },
      /** @deprecated Prefer FormField + FormSelect / TeboxSelectDropdown */
    },
    button: {
      // Main action buttons use bodySmall typography (12px) with font weight 500.
      // Medium height 40px; inputs use materialOutlinedField (48px legacy field rhythm).
      // See: IOSButton, tebox-action-button.css for brutalist offset hover.
      sizes: {
        small: { height: '29px', padding: '6px 14px', fontSize: '12px' },
        medium: {
          height: '40px',
          padding: '10px 18px',
          fontSize: '12px',
        },
        large: { height: '52px', padding: '14px 22px', fontSize: '12px' }
      },
      primary: {
        backgroundColor: 'var(--color-accent-primary)',
        color: '#000000',
        border: '1px solid #000000',
        borderRadius: RADIUS_CONTAINER,
        padding: '10px 18px', // matches sizes.medium
        fontSize: '12px', // bodySmall typography
        fontWeight: '500',
        fontFamily: APP_FONT, // Buttons use Inter
        cursor: 'pointer',
        transition: 'all 0.1s ease'
      },
      secondary: {
        backgroundColor: '#FFFFFF',
        color: '#000000',
        /** Black chrome on buttons only — structural UI still uses `colors.border.light` grey. */
        border: '1px solid #000000',
        borderRadius: RADIUS_CONTAINER,
        padding: '10px 18px', // matches sizes.medium
        fontSize: '12px', // bodySmall typography
        fontWeight: '500',
        fontFamily: APP_FONT, // Buttons use Inter
        cursor: 'pointer',
        transition: 'all 0.1s ease'
      }
    },
    card: {
      // Cards should have a visible surface on white pages (use the shared tertiary surface token).
      backgroundColor: 'var(--color-background-tertiary, #FAFAF9)',
      border: `1px solid ${BORDER_FIELD}`,
      borderRadius: RADIUS_CONTAINER,
      padding: '16px'
    },
    modal: {
      borderRadius: RADIUS_CONTAINER,
      padding: '24px'
    },
    /**
     * Multi-section forms in staff drawers (e.g. product editor).
     * Surfaces: primary white, `border.field`, soft chrome radius — matches cards / modals in TEBOX.
     * Section headings: `typography.formCardSectionTitle` + semantic `h4` under the drawer header.
     */
    drawerForm: {
      /** Scrollable body inset (matches `spacing.lg`) */
      scrollPadding: '24px',
      /** Vertical gap between bordered section cards */
      sectionStackGap: '24px',
      /** Vertical gap between fields inside a section */
      fieldStackGap: '16px',
      /** Centered column when no onboarding rail */
      mainMaxWidthPx: 920,
      /** Catalog image column (desktop) */
      mediaThumbWidthPx: 220,
      /** Centered thumb on narrow viewports */
      mediaThumbMaxWidthXsPx: 300,
      section: {
        border: `1px solid ${BORDER_FIELD}`,
        borderRadius: RADIUS_CONTAINER,
        backgroundColor: '#FFFFFF',
        padding: '24px'
      },
      /** Two-column split heading inside a section */
      columnTitle: {
        fontFamily: APP_FONT,
        fontSize: '11px',
        fontWeight: 500,
        lineHeight: 1.4,
        letterSpacing: '0.05em',
        textTransform: 'none' as const,
        color: '#021B1B'
      },
      /** Onboarding rail: sticky column beside form */
      onboardingRail: {
        flexBasis: '360px',
        maxWidth: '400px',
        about: {
          padding: '24px',
          backgroundColor: '#FAFAF9',
          border: `1px solid ${BORDER_FIELD}`,
          borderRadius: RADIUS_CONTAINER
        },
        proTip: {
          padding: '24px',
          backgroundColor: '#F3F4F6',
          border: `1px solid ${BORDER_FIELD}`,
          borderLeft: `3px solid #2E96FF`,
          borderRadius: RADIUS_CONTAINER
        }
      }
    },
    /**
     * Underline nav tabs (Insights, Brand preview, customer profile, wallet ledger header, etc.).
     * Tab label typography matches `typography.body` (same as BackNavigation link text).
     * Not for segmented controls (filled pill groups) — those stay 12px per context.
     */
    tabs: {
      tab: {
        fontFamily: APP_FONT,
        fontSize: '13px',
        lineHeight: 1.5,
        letterSpacing: 'normal',
        fontWeight: 400,
        fontWeightActive: 400,
        padding: '8px 0'
      },
      indicator: {
        height: '2px',
        color: '#000000'
      }
    },
    /**
     * Settings-screen toggle (`components/ui/ToggleSwitch.tsx`).
     * Pill track (`border-radius` 20px); thumb is fully rounded (`border-radius` 22px over 16×16 → circular). On: black track (`colors.primary`) + `colors.primaryLight` border; white thumb.
     * Off: white track (`background.primary`), 1px black border; optional label matches `typography.caption`.
     */
    toggleSwitch: {
      track: {
        width: '37px',
        height: '20px',
        borderRadius: '20px'
      },
      thumb: {
        width: '16px',
        height: '16px',
        borderRadius: '22px'
      },
      on: {
        backgroundColor: '#000000',
        border: '1px solid #1a1a1a'
      },
      label: {
        fontFamily: APP_FONT,
        fontSize: '11px',
        fontWeight: 400 as const,
        lineHeight: 1.4,
        letterSpacing: '0.04em' as const,
        color: '#021B1B'
      }
    },
    floatingToolbar: {
      backgroundColor: '#FFFFFF',
      borderRadius: RADIUS_CONTAINER,
      padding: '12px 16px',
      boxShadow: 'none',
      border: `1px solid ${BORDER_FIELD}`, // Matches form field border (colors.border.field)
      height: '48px',
      minHeight: '48px',
      maxHeight: '48px',
      backdropFilter: 'blur(10px)',
      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
      typography: {
        banner: {
          fontSize: '11px',
          fontWeight: 500,
          fontFamily: APP_FONT,
          color: '#021B1B'
        },
        searchInput: {
          fontSize: '13px',
          fontWeight: 500,
          fontFamily: APP_FONT,
          color: '#000000'
        },
        dropdownItem: {
          fontSize: '12px',
          fontWeight: 500,
          fontFamily: APP_FONT,
          color: '#021B1B'
        }
      },
      /**
       * Transient activity banners above the tee-sheet toolbar (`toolbar-action-completed`, etc.).
       * Success-style events share the Tebox accent surface; neutral and error variants use status tokens.
       */
      actionBannerStyles: {
        block: FLOATING_TOOLBAR_ACTION_BANNER_SUCCESS,
        unblock: FLOATING_TOOLBAR_ACTION_BANNER_SUCCESS,
        promotion: FLOATING_TOOLBAR_ACTION_BANNER_SUCCESS,
        'promotion-removed': FLOATING_TOOLBAR_ACTION_BANNER_NEUTRAL,
        recurrence: FLOATING_TOOLBAR_ACTION_BANNER_SUCCESS,
        payment: FLOATING_TOOLBAR_ACTION_BANNER_SUCCESS,
        status: FLOATING_TOOLBAR_ACTION_BANNER_SUCCESS,
        delete: FLOATING_TOOLBAR_ACTION_BANNER_ERROR,
        group: FLOATING_TOOLBAR_ACTION_BANNER_SUCCESS,
        reservation: FLOATING_TOOLBAR_ACTION_BANNER_SUCCESS,
        error: FLOATING_TOOLBAR_ACTION_BANNER_ERROR,
        default: FLOATING_TOOLBAR_ACTION_BANNER_SUCCESS,
      }
    },
    /**
     * Left rail + settings corridor destination labels.
     * See: TEBOX_DESIGN_SYSTEM.html #layout-sidebar-nav, #components-settings-corridor
     */
    sidebarNav: {
      label: SIDEBAR_NAV_LABEL,
    },
    /**
     * PILL COMPONENT
     *
     * Unified pill/badge styling for status, category, promo, visibility, etc.
     * Used by: PromoPill, PricePill, VisibilityPill, OnlineBookingPill, CategoryPill, StandardPill, StatusPill
     *
     * Color treatment: **Reference chip** (warm cream + brown-gold text) applies only when the accent is
     * **neutral** (`pill.colors.neutral`). All other accents use a **soft tint** from that hue (`lightenColor`) + `darkenColor`
     * for labels — flat, hairline-free treatment, hue-specific fills. `filled` uses a slightly stronger tint than default.
     * Override lighten via StandardPill `backgroundLighten` / getStandardPillStyles 4th arg.
     * See: docs/design-system/TEBOX_DESIGN_SYSTEM.html — Pills & badges (StandardPill), Club activity drawer, table pills
     */
    pill: {
      /** Warm cream pill shell — reference treatment for default chips (e.g. informational / neutral rows). */
      surfaceWarm: '#F9F6F1',
      /** Muted brown-gold on surfaceWarm for neutral/informational pill labels. */
      inkWarm: '#8B7E66',
      /** Mix accent toward white for `filled` variant only (0–1). */
      backgroundLighten: 0.93,
      base: {
        display: 'inline-flex',
        alignItems: 'center',
        gap: 6,
        fontFamily: APP_FONT,
        fontWeight: 500,
        lineHeight: 1,
        border: 'none',
        cursor: 'default',
        userSelect: 'none' as const
      },
      sizes: {
        /** Tee-sheet compact row empty cells (~38px tall). */
        compact: {
          padding: '2px 6px',
          fontSize: '12px'
        },
        small: {
          padding: '4px 10px',
          fontSize: PILL_FONT_SIZE
        },
        medium: {
          padding: '4px 12px',
          fontSize: PILL_FONT_SIZE
        }
      },
      borderRadius: RADIUS_CONTAINER,
      // Color hex values for pill variants (use lightenColor/darkenColor for actual styles)
      colors: {
        /** Promo wash accent — drives pastel pill fill (`lightenColor`). Original deep red. */
        promo: '#C62828',
        /** Promo pill / tee-sheet promo labels — saturated scarlet on the pale promo wash. */
        promoInk: '#FF3B42',
        success: ACCENT_SUCCESS_HEX,
        warning: '#d97706',
        error: '#dc2626',
        info: '#0284c7',
        neutral: '#6b7280',
        visibility: { public: ACCENT_SUCCESS_HEX, private: '#6b7280' },
        booking: { enabled: '#0284c7', disabled: FORM_FIELD_LABEL_HEX },
        category: {
          'golf-round': ACCENT_SUCCESS_HEX,
          'cart-rental': '#0284c7',
          membership: '#9333ea',
          services: '#d97706',
          extras: '#dc2626',
          default: '#6b7280'
        },
        /** Guest golfer dashboard — semantic reservation / instalment pills (avoid club primary for all statuses). */
        golferStatus: {
          /** Pending / draft tee times — true orange (distinct from amber `warning` used for partials). */
          pendingReservation: '#EA580C',
          /** Cancelled / no-show — rose, complements `success` for paid rounds. */
          cancelledReservation: '#BE123C',
        }
      }
    },
    /**
     * Settings/reporting/marketing landing rails — icon-leading category tiles.
     * Implementation: `LandingPageCard` in `apps/web/src/components/design-system/LandingPageCard.tsx`.
     * Docs: `#components-landing-cards` in `TEBOX_DESIGN_SYSTEM.html`.
     */
    landingPageCard: {
      /** Horizontal inset for title/description stack beside the leading icon */
      contentRailPaddingInline: '10px',
      /** Circular icon slot — matches marketing/coupon flow step headers (`MarketingAutomationFlowStep`). */
      icon: {
        sizePx: 36,
        glyphSizePx: 18,
        /** Warm off-white — landing tiles only (not flow-step lime `accent.primary`). */
        backgroundColor: '#FAF9F8',
      },
    },
    /**
     * Selectable grid tile: full-area button surface for modal pickers and fork screens.
     * Implementation: `ChoiceTileButton` in `apps/web/src/components/design-system/ChoiceTileButton.tsx`.
     * Used by Customers purchase gate and Marketing Whats in the Bag presets. Inner layout stays in callers.
     * Docs: `#components-choice-tile` in `TEBOX_DESIGN_SYSTEM.html`.
     */
    choiceTile: {
      elevationTransition: 'box-shadow 0.15s ease, border-color 0.15s ease',
    },
    /**
     * ApexCharts segment fills — accent palette only (no semantic success/warning greens).
     * Two-segment donuts: `CustomerCategoryDistributionChart`, brand margin mix, vendor spend mix.
     */
    charts: {
      donutTwoSegment: [...CHART_DONUT_TWO_SEGMENT],
      donutTwoSegmentDataLabelColors: [...CHART_DONUT_TWO_SEGMENT_DATA_LABEL_COLORS],
      donutMultiSegment: [...CHART_DONUT_MULTI_SEGMENT],
      legendMarker: {
        width: 12,
        height: 12,
        radius: 0,
      },
    },
    /** @deprecated Use components.pill instead */
    badge: {
      base: {
        display: 'inline-flex',
        alignItems: 'center',
        padding: '4px 8px',
        borderRadius: RADIUS_CONTAINER,
        fontSize: PILL_FONT_SIZE,
        fontWeight: 500,
        lineHeight: 1,
        fontFamily: APP_FONT
      },
      promo: {
        backgroundColor: 'rgba(198, 40, 40, 0.12)',
        color: 'rgba(198, 40, 40, 0.9)',
        fontWeight: 500,
        border: '1px solid rgba(198, 40, 40, 0.25)'
      }
    }
  }};

// Type definition for the design system
export interface TeboxDesignSystem {
  colors: {
    primary: string;
    primaryLight: string;
    background: Record<string, string>;
    text: Record<string, string>;
    accent: Record<string, string>;
    border: Record<string, string>;
    gray: Record<string, string>;
    table: Record<string, string>;
    separator: Record<string, string>;
    status: Record<string, string>;
    teeSheet: Record<string, string>;
  };
  spacing: Record<string, string>;
  typography: Record<string, Record<string, any>>;
  borderRadius: Record<string, string>;
  shadows: Record<string, string>;
  components: {
    formInput: {
      base: Record<string, any>;
      focus: Record<string, any>;
      hover: Record<string, any>;
    };
    formSelect: {
      base: Record<string, any>;
    };
    teboxTextField: {
      label: Record<string, any>;
      input: Record<string, any>;
      container: Record<string, any>;
      multiline: Record<string, any>;
    };
    button: Record<string, any>;
    card: Record<string, any>;
    modal: Record<string, any>;
    floatingToolbar: Record<string, any>;
    activityTimeline: Record<string, any>;
    sidebarNav: {
      label: {
        fontFamily: string;
        fontSize: string;
        fontWeight: number;
        lineHeight: number;
        letterSpacing: string;
      };
    };
    tabs: Record<string, any>;
    toggleSwitch: Record<string, any>;
    pill: {
      backgroundLighten: number;
      surfaceWarm: string;
      inkWarm: string;
      base: Record<string, any>;
      sizes: Record<string, Record<string, string>>;
      borderRadius: string;
      colors: Record<string, any>;
    };
    landingPageCard: {
      contentRailPaddingInline: string;
      icon: {
        sizePx: number;
        glyphSizePx: number;
        backgroundColor: string;
      };
    };
    choiceTile: {
      elevationTransition: string;
    };
    badge: {
      base: Record<string, any>;
      promo: Record<string, any>;
    };
    layoutHeader: {
      chromePaddingBlock: string;
      chromePaddingInline: string;
      titleBlockPadding: string;
      titleToSubtextGap: string;
      titleStackLineHeight: number;
      noDuplicateInPageBody: readonly ['routeTitle', 'routeSubtitle', 'primaryHeaderAction'];
    };
    icon: {
      strokeStandard: number;
      strokeHairline: number;
      strokeEmphasis: number;
      strokeGolfCart: number;
    };
    teeSheetRow: {
      timeInterval: {
        expanded: {
          fontSize: string;
          fontWeightHourMark: number;
          fontWeightInterval: number;
        };
        compact: {
          fontSize: string;
          fontWeightHourMark: number;
          fontWeightInterval: number;
        };
        clickPadding: string;
      };
      playerName: {
        fontFamily: string;
        fontSize: string;
        fontWeight: number;
        lineHeight: string;
        letterSpacing: string;
      };
    };
    emptyState: {
      foregroundContentOpacity: number;
      backgroundIconOpacity: number;
    };
    standardTable: {
      contentGutter: string;
      density: {
        comfortable: Record<string, string>;
        compact: Record<string, string>;
      };
      headerFontWeight: number;
      rowNameFontWeight: number;
    };
    tableTotalDisplay: {
      defaultAmountColor: string;
      sizes: Record<
        'lg' | 'md' | 'sm',
        { integer: Record<string, string | number>; decimal: Record<string, string | number> }
      >;
    };
    tableKpiCard: Record<string, any>;
    customerProfileKpiTile: {
      padding: string;
      valueFontWeight: number;
      valueTypography: Record<string, string | number>;
    };
    kpiStrip: {
      columnGap: string;
      plainPaddingBlock: string;
      panelBorderRadius: string;
      panelCellPaddingInline: string;
      panelTitleToSubtitleGap: string;
      panelSubtitleToValueGap: string;
      panelValueTrendGap: string;
      panelValueRowHeight: string;
      panelTrendClusterGap: string;
      panelMenuButtonSize: string;
      borderedCellPaddingBlock: string;
      borderedCellPaddingInline: string;
      labelToValueGap: string;
      valueToSubtextGap: string;
      deltaChipMarginTop: string;
      accentCycle: readonly ('lime' | 'sky' | 'mint' | 'amber' | 'violet' | 'rose')[];
      accents: Record<
        'lime' | 'sky' | 'mint' | 'amber' | 'violet' | 'rose' | 'neutral',
        { background: string; border: string; hoverBackground: string }
      >;
    };
    dropdownMenu: {
      offsetBelowField: string;
      offsetBelowTrigger: string;
      maxHeight: string;
      zIndex: number;
      portalZIndex: number;
      minWidth: string;
      rowPaddingX: string;
      rowPaddingY: string;
      sectionPaddingX: string;
      sectionPaddingY: string;
    };
    tableHeaderFilterBar: {
      bar: {
        alignItems: string;
        gap: string;
        marginBottom: string;
      };
      field: {
        paddingTop: string;
        paddingBottom: string;
        labelMarginBottom: string;
        valueRowMinHeight: string;
        valueRowGap: string;
        valueFontWeight: number;
      };
      menu: {
        offsetBelowField: string;
        offsetBelowTrigger: string;
        maxHeight: string;
        zIndex: number;
        minWidth: string;
        rowPaddingX: string;
        rowPaddingY: string;
        sectionPaddingX: string;
        sectionPaddingY: string;
      };
      clearButton: {
        marginBottom: string;
        minWidth: string;
      };
      searchFieldFlex: number;
      defaultFieldFlex: number;
      searchMinWidthMd: string;
      filterMinWidthMd: string;
    };
    carIcon: {
      kpiCardCornerGlyph: number;
      headerKpi: number;
      kpiPopover: number;
    };
    /** Reporting / ledger table header row — see #components-financial-table-header in TEBOX_DESIGN_SYSTEM.html */
    financialTableHeader: Record<string, any>;
    settingsPanel: Record<string, any>;
    /** Bordered sections + onboarding rail for staff drawer forms */
    drawerForm: Record<string, any>;
    simpleDatePicker: Record<string, any>;
    formCheckbox: {
      size: string;
      boxBorderRadius: string;
      uncheckedBackgroundColor: string;
      uncheckedBorderColor: string;
      checkedBackgroundColor: string;
      checkmarkImage: string;
      checkBackgroundSize: string;
      focusOutline: string;
      focusOutlineOffset: string;
      labelAlignMarginTop: string;
    };
    colorPicker: {
      swatchSize: string;
      swatchSizeCompact: string;
      previewSize: string;
      gridColumns: number;
      gridGap: string;
      panelPadding: string;
      sectionGap: string;
    };
    /** Row + chip styles for Sun–Sat (or full day name) toggles */
    dayOfWeekToggle: {
      row: Record<string, string>;
      button: Record<string, string | number>;
      selected: Record<string, string>;
      unselected: Record<string, string>;
      hoverUnselectedBackground: string;
    };
  };
}

